import type { IncompleteDetailsTarget, WorklistProcedureKind } from "../api/worklistApi";
import type { WorklistItem, WorklistItemKind } from "./buildWorklistItems";
import type { WorklistFormSlot } from "./worklistFormSlot";

export function procedureKindFromTaskLabel(task: string): WorklistProcedureKind {
    const lower = task.toLowerCase();
    if (lower.includes("surgery")) return "surgery";
    if (lower.includes("shockwave")) return "shockwave";
    if (lower.includes("injection")) return "injection";
    return "procedure";
}

export function detailsTargetFromDismissal(dismissal: WorklistDismissalRow): IncompleteDetailsTarget | null {
    if (dismissal.kind !== "event_details" || !dismissal.event_id) return null;
    const procedureKind = procedureKindFromTaskLabel(dismissal.task_label);
    if (procedureKind === "procedure") return null;
    return {
        eventId: dismissal.event_id,
        trackerId: dismissal.tracker_id ?? "none",
        procedureKind,
    };
}

export type WorklistDismissalKind = "form" | "event_details" | "consent";

export interface WorklistDismissalRow {
    worklist_dismissal_id: string;
    user_id: string;
    diary_id: string;
    kind: WorklistDismissalKind;
    tracker_id: string | null;
    schedule_id: string | null;
    schedule_index: number | null;
    event_id: string | null;
    consent_id: string | null;
    task_label: string;
    patient_name: string | null;
    hospital_number: string | null;
    organisation_name: string | null;
    due_at: string | null;
    dismissed_at: string;
}

export interface WorklistDismissalKey {
    kind: WorklistItemKind;
    diaryId: string;
    trackerId: string | null;
    scheduleId: string | null;
    scheduleIndex: number | null;
    eventId: string | null;
    consentId: string | null;
}

export function worklistItemDismissalKey(item: WorklistItem): WorklistDismissalKey {
    return {
        kind: item.kind,
        diaryId: item.diaryId,
        trackerId: item.trackerId,
        scheduleId: item.scheduleId,
        scheduleIndex: item.scheduleIndex,
        eventId: item.detailsTarget?.eventId ?? null,
        consentId: item.consentId,
    };
}

export function dismissalMatchesKey(dismissal: WorklistDismissalRow, key: WorklistDismissalKey): boolean {
    if (dismissal.kind !== key.kind || dismissal.diary_id !== key.diaryId) return false;
    if (key.kind === "form") {
        return (
            dismissal.tracker_id === key.trackerId &&
            dismissal.schedule_id === key.scheduleId &&
            dismissal.schedule_index === key.scheduleIndex
        );
    }
    if (key.kind === "consent") {
        return Boolean(key.consentId) && dismissal.consent_id === key.consentId;
    }
    return Boolean(key.eventId) && dismissal.event_id === key.eventId;
}

export function findDismissalForItem(
    dismissals: WorklistDismissalRow[],
    item: WorklistItem
): WorklistDismissalRow | undefined {
    const key = worklistItemDismissalKey(item);
    return dismissals.find((dismissal) => dismissalMatchesKey(dismissal, key));
}

export function partitionOpenAndArchivedItems(
    openCandidates: WorklistItem[],
    dismissals: WorklistDismissalRow[]
): { openItems: WorklistItem[]; archivedFromOpen: WorklistItem[] } {
    const openItems: WorklistItem[] = [];
    const archivedFromOpen: WorklistItem[] = [];
    const matchedDismissalIds = new Set<string>();

    for (const item of openCandidates) {
        const dismissal = findDismissalForItem(dismissals, item);
        if (dismissal) {
            matchedDismissalIds.add(dismissal.worklist_dismissal_id);
            archivedFromOpen.push({
                ...item,
                dismissalId: dismissal.worklist_dismissal_id,
                dismissedAt: dismissal.dismissed_at,
                action: "restore",
            });
            continue;
        }
        openItems.push(item);
    }

    return { openItems, archivedFromOpen };
}

/** Build archived rows for dismissals that no longer match an open candidate. */
export function buildOrphanArchivedItems(
    dismissals: WorklistDismissalRow[],
    matchedFromOpen: WorklistItem[],
    labels: { unknownPatient: string; procedureFallback: string }
): WorklistItem[] {
    const matchedIds = new Set(
        matchedFromOpen.map((item) => item.dismissalId).filter((id): id is string => Boolean(id))
    );

    return dismissals
        .filter((dismissal) => !matchedIds.has(dismissal.worklist_dismissal_id))
        .map((dismissal) => dismissalToWorklistItem(dismissal, labels));
}

export function dismissalToWorklistItem(
    dismissal: WorklistDismissalRow,
    labels: { unknownPatient: string; procedureFallback: string }
): WorklistItem {
    const isForm = dismissal.kind === "form";
    const isConsent = dismissal.kind === "consent";
    return {
        id: isForm
            ? `${dismissal.diary_id}:form:${dismissal.tracker_id}:${dismissal.schedule_id}:${dismissal.schedule_index}`
            : isConsent
              ? `${dismissal.diary_id}:consent:${dismissal.consent_id}`
              : `${dismissal.diary_id}:event_details:${dismissal.event_id}`,
        kind: dismissal.kind,
        task: dismissal.task_label || labels.procedureFallback,
        patientName: dismissal.patient_name?.trim() || labels.unknownPatient,
        hospitalNumber: dismissal.hospital_number,
        organisationName: dismissal.organisation_name,
        diaryId: dismissal.diary_id,
        trackerId: dismissal.tracker_id,
        scheduleId: dismissal.schedule_id,
        scheduleIndex: dismissal.schedule_index,
        status: isForm || isConsent ? "due" : "incomplete",
        dueAt: dismissal.due_at,
        blockedBy: null,
        action: "restore",
        detailsTarget: detailsTargetFromDismissal(dismissal),
        consentId: dismissal.consent_id,
        dismissalId: dismissal.worklist_dismissal_id,
        dismissedAt: dismissal.dismissed_at,
    };
}

export function insertPayloadFromItem(
    item: WorklistItem,
    userId: string
): Omit<WorklistDismissalRow, "worklist_dismissal_id" | "dismissed_at"> {
    const slot: WorklistFormSlot | null =
        item.kind === "form" && item.scheduleId != null && item.scheduleIndex != null
            ? { scheduleId: item.scheduleId, scheduleIndex: item.scheduleIndex }
            : null;

    return {
        user_id: userId,
        diary_id: item.diaryId,
        kind: item.kind,
        tracker_id: item.kind === "form" ? item.trackerId : null,
        schedule_id: slot?.scheduleId ?? null,
        schedule_index: slot?.scheduleIndex ?? null,
        event_id: item.kind === "event_details" ? (item.detailsTarget?.eventId ?? null) : null,
        consent_id: item.kind === "consent" ? item.consentId : null,
        task_label: item.task,
        patient_name: item.patientName,
        hospital_number: item.hospitalNumber,
        organisation_name: item.organisationName,
        due_at: item.dueAt,
    };
}
